<script>
import Layout from '@/router/layouts/main';
import Grid from './components/grid.vue';
import Multiselect from 'vue-multiselect';
import statusMixin from '@/mixins/statusMixin';
import StatusMultiselect from '@/components/widgets/multiselects/status';
import FiltersCard from '@/components/widgets/cards/filters-search';
import FieldsMenu from '@/components/widgets/grids/grid-fields-menu.vue';

export default {
  page: {
    title: 'Receptions',
  },
  mixins: [statusMixin],
  components: {
    Layout,
    Grid,
    Multiselect,
    StatusMultiselect,
    FiltersCard,
    FieldsMenu,
  },
  data() {
    return {
      filters: null,
      searchText: '',
      showFilters: false,
    };
  },
  created() {
    this.search();
  },
  watch: {
    $route() {
      this.search();
    },
  },
  methods: {
    search() {
      this.filters = {
        statusCodes: this.selectedStatusIds,
        suppliersIdPublic: this.selectedSuppliersIds,
        showClosed: this.showClosed,
        search: this.searchText,
      };
    },
    cancelSearch() {
      this.selectedStatus = [];
      this.selectedSuppliers = [];
      this.searchText = '';
      this.showClosed = false;

      this.search();
    },
    statusSelectionChanged(statusList) {
      this.selectedStatus = statusList;

      if (
        this.selectedStatus.filter((x) => x.statusCode == 'CLOSED').length > 0
      ) {
        this.showClosed = true;
      }
    },
    showClosedChanged(checked) {
      if (!checked) {
        this.selectedStatus = [
          ...this.selectedStatus.filter((x) => x.statusCode != 'CLOSED'),
        ];
      } else if (
        this.selectedStatus.length > 0 &&
        this.selectedStatus.findIndex((x) => x.statusCode == 'CLOSED') < 0
      ) {
        this.selectedStatus = [
          ...this.selectedStatus,
          {
            statusCode: 'CLOSED',
            statusName: 'orders.closed',
            statusVariant: 'secondary',
            statusOrder: 4,
          },
        ];
      }
    },
  },
  computed: {
    fieldsComputed() {
      return [
        {
          key: 'receptionDate',
          label: this.$t('orders.receptionDate'),
        },
        {
          key: 'externalNumber',
          label: this.$t('orders.externalNumber'),
        },
        {
          key: 'number',
          label: this.$t('general.number'),
        },
        {
          key: 'statusOrder',
          label: this.$t('general.status'),
          class: 'centered-field',
        },
        {
          key: 'totalItems',
          label: this.$t('items.items'),
          class: 'centered-field',
        },
        {
          key: 'supplierName',
          label: this.$t('suppliers.supplier'),
          class: 'centered-field',
        },
        {
          key: 'shippingLocationName',
          label: this.$t('general.shippingLocation'),
          sortable: true,
          class: 'centered-field',
        },
        {
          key: 'received',
          label: this.$t('receptions.reception'),
          class: 'centered-field',
        },
      ];
    },
    suppliers() {
      return this.$store.state.cache.suppliers;
    },
    receptionsFiltersConfig: {
      get() {
        return this.$store.getters['localstorage/getByCode'](
          'receptionsFiltersConfig'
        );
      },
      set(value) {
        this.$store.dispatch('localstorage/setByCode', {
          code: 'receptionsFiltersConfig',
          config: value,
        });
      },
    },
    selectedStatus: {
      get() {
        return this.receptionsFiltersConfig.selectedStatus;
      },
      set(value) {
        this.receptionsFiltersConfig = {
          ...this.receptionsFiltersConfig,
          selectedStatus: value,
        };
      },
    },
    showClosed: {
      get() {
        return this.receptionsFiltersConfig.showClosed;
      },
      set(value) {
        this.receptionsFiltersConfig = {
          ...this.receptionsFiltersConfig,
          showClosed: value,
        };
      },
    },
    selectedSuppliers: {
      get() {
        return this.receptionsFiltersConfig.selectedSuppliers;
      },
      set(value) {
        this.receptionsFiltersConfig = {
          ...this.receptionsFiltersConfig,
          selectedSuppliers: value,
        };
      },
    },
    selectedSuppliersIds() {
      return this.selectedSuppliers != null
        ? this.selectedSuppliers.map((a) => a.idPublic)
        : [];
    },
    selectedStatusIds() {
      return this.selectedStatus != null
        ? this.selectedStatus.map((a) => a.statusCode)
        : [];
    },
  },
};
</script>

<template>
  <Layout
    :title="$t('receptions.receptions')"
    :no-card="true"
    @esc-double-pressed="cancelSearch"
  >
    <div class="flex-box">
      <b-input-group class="search-bar custom-inventory-shadow">
        <b-form-input
          id="searchInput"
          type="text"
          style="border: none"
          :placeholder="$t('general.search')"
          @keydown.escape.prevent="cancelSearch()"
          @keydown.enter.prevent="search()"
          v-model="searchText"
        ></b-form-input>
        <b-input-group-append>
          <b-button
            style="border: none"
            variant="outline-light"
            @click="search()"
          >
            <i class="bx bx-search"></i>
          </b-button>
        </b-input-group-append>
      </b-input-group>

      <FieldsMenu
        id="receptions-grid"
        class="flex-push"
        :dropup="true"
        :fields="fieldsComputed"
      />
    </div>

    <FiltersCard @search="search" @cancel="cancelSearch">
      <template #row>
        <b-row>
          <b-col sm="12" lg="4">
            <b-form-group
              id="group-suppliers"
              :label="$t('suppliers.suppliers')"
              label-class="no-bold"
              label-for="supplierSelect"
            >
              <multiselect
                id="supplierSelect"
                ref="multiselectSupplier"
                v-model="selectedSuppliers"
                :options="suppliers"
                :label="'name'"
                track-by="idPublic"
                :placeholder="$t('general.pickAValue')"
                @remove="multiselectClosed($refs.multiselectSupplier)"
                :multiple="true"
                :show-labels="false"
                :hide-selected="true"
                :close-on-select="false"
                :clear-on-select="false"
                :searchable="false"
              >
                <template v-slot:noOptions>
                  {{ $t('general.noResult') }}
                </template>
                <template slot="tag" slot-scope="{ option, remove }">
                  <span
                    class="custom__tag badge badge-primary badge-pill"
                    style="font-size: 1em; margin-right: 5px; cursor: pointer"
                    v-html="option.name"
                    @click="remove(option)"
                  >
                  </span>
                </template>
              </multiselect>
            </b-form-group>
          </b-col>

          <b-col sm="12" lg="4">
            <b-form-group
              id="group-status"
              :label="$t('general.status')"
              label-class="no-bold"
              label-for="statusSelect"
            >
              <StatusMultiselect
                :list="getReceptionStatus()"
                :defaultSelectedList="selectedStatus"
                @selection-change="(l) => statusSelectionChanged(l)"
              />
            </b-form-group>
          </b-col>

          <b-col sm="12" lg="4" class="flex-vertical-align-bottom">
            <div style="padding-bottom: 18px">
              <b-form-checkbox
                id="checkbox-show-closed"
                v-model="showClosed"
                name="checkbox-show-closed"
                @change="showClosedChanged"
                switch
              >
                <span class="no-bold">{{ $t('orders.showClosed') }}</span>
              </b-form-checkbox>
            </div>
          </b-col>
        </b-row>
      </template>
    </FiltersCard>

    <Grid v-if="filters != null" :filters="filters" :fields="fieldsComputed" />
  </Layout>
</template>

<style scoped>
.search-bar {
  max-width: 500px;
  max-height: 40px;
  background-color: white;
  border-radius: 5px;
}
</style>
